import { useEffect, useMemo, useState, useRef } from "react";
import api from "@/lib/api";
import { Bell, AlertCircle, Clock, Ghost, X, CheckCheck } from "lucide-react";
import { STATUS_COLORS } from "@/lib/constants";
import LeadDetail from "@/pages/LeadDetail";
import { useAuth } from "@/context/AuthContext";

/**
 * "Mark as read" model:
 * We store a per-user localStorage map: { [leadId]: signature }
 * where signature = `${follow_up_date || ""}|${last_activity_at || ""}`.
 * If the lead's signature later changes (new follow-up date, activity logged, etc.)
 * the alert reappears — because the underlying state changed.
 */
const READ_KEY = (uid) => `leadflow_alerts_read_${uid || "anon"}`;
const sigOf = (l) => `${l.follow_up_date || ""}|${l.last_activity_at || ""}`;

const loadRead = (uid) => {
  try { return JSON.parse(localStorage.getItem(READ_KEY(uid)) || "{}"); }
  catch { return {}; }
};
const saveRead = (uid, map) => localStorage.setItem(READ_KEY(uid), JSON.stringify(map));

export default function NotificationBell() {
  const { user } = useAuth();
  const isAdmin = user?.role === "superadmin";
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ overdue: [], today: [], abandoned: [], counts: {} });
  const [detail, setDetail] = useState(null);
  const [members, setMembers] = useState([]);
  const [readMap, setReadMap] = useState(() => loadRead(user?.id));
  const ref = useRef(null);

  const load = () => api.get("/alerts").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (isAdmin) api.get("/users").then((r) => setMembers(r.data)).catch(() => {});
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [isAdmin]);

  useEffect(() => { setReadMap(loadRead(user?.id)); }, [user?.id]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isRead = (lead) => readMap[lead.id] === sigOf(lead);

  const markRead = (lead) => {
    const next = { ...readMap, [lead.id]: sigOf(lead) };
    setReadMap(next);
    saveRead(user?.id, next);
  };

  const markAllRead = () => {
    const next = { ...readMap };
    [...data.overdue, ...data.today, ...data.abandoned].forEach((l) => {
      next[l.id] = sigOf(l);
    });
    setReadMap(next);
    saveRead(user?.id, next);
  };

  // Filter out read alerts
  const filtered = useMemo(() => {
    const clean = (arr) => (arr || []).filter((l) => !isRead(l));
    return {
      overdue: clean(data.overdue),
      today: clean(data.today),
      abandoned: clean(data.abandoned),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, readMap]);

  const total = filtered.overdue.length + filtered.today.length + filtered.abandoned.length;
  const hasAny = total > 0;

  const Section = ({ title, icon: Icon, items, testid, tone = "neutral" }) => (
    <div data-testid={testid} className="py-2">
      <div className="flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-widest text-slate-500">
        <Icon className={`w-3.5 h-3.5 ${tone === "danger" ? "text-rose-500" : tone === "warn" ? "text-amber-500" : "text-slate-400"}`} />
        <span>{title}</span>
        <span className="ml-auto text-slate-400">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="px-3 py-2 text-xs text-slate-400 italic">No items</p>
      ) : (
        <div className="space-y-0.5">
          {items.slice(0, 5).map((l) => (
            <div
              key={l.id}
              className="group relative flex items-start hover:bg-slate-50 transition-colors"
              data-testid={`alert-item-${l.id}`}
            >
              <button
                onClick={() => { setDetail(l); setOpen(false); }}
                className="w-full text-left px-3 py-2 pr-9"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium truncate text-slate-800">{l.name}</p>
                  <span className={STATUS_COLORS[l.status]}>{l.status}</span>
                </div>
                {l.follow_up_date && <p className="text-[11px] text-slate-500 mt-0.5">FU: {l.follow_up_date}</p>}
                {tone === "neutral" && l.last_activity_at && !l.follow_up_date && (
                  <p className="text-[11px] text-slate-500 mt-0.5">Idle since {new Date(l.last_activity_at).toLocaleDateString()}</p>
                )}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); markRead(l); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                aria-label={`Mark ${l.name} as read`}
                data-testid={`alert-mark-read-${l.id}`}
                title="Mark as read"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-8 h-8 rounded-md hover:bg-white/10 flex items-center justify-center transition-colors"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4 text-white" />
        {total > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-semibold flex items-center justify-center"
            data-testid="notification-count"
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed sm:absolute inset-x-2 sm:inset-x-auto sm:right-0 top-14 sm:top-11 sm:w-96 max-h-[70vh] sm:max-h-[520px] rounded-lg overflow-y-auto z-50 bg-white border border-slate-200 shadow-xl divide-y divide-slate-100"
          data-testid="notification-panel"
        >
          <div className="px-4 pt-4 pb-3 flex items-start justify-between gap-3">
            <div>
              <p className="text-base font-semibold text-slate-900">Alerts</p>
              <p className="text-[11px] text-slate-500">Stay on top of leads that need action</p>
            </div>
            {hasAny && (
              <button
                type="button"
                onClick={markAllRead}
                className="text-[11px] font-medium text-[#1D63ED] hover:underline flex items-center gap-1 flex-shrink-0 mt-0.5"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Mark all as read
              </button>
            )}
          </div>
          {!hasAny ? (
            <p className="px-4 py-6 text-center text-sm text-slate-400 italic" data-testid="alerts-empty">
              You're all caught up. 🎉
            </p>
          ) : (
            <>
              <Section title="Overdue follow-ups" icon={AlertCircle} items={filtered.overdue} testid="alerts-overdue" tone="danger" />
              <Section title="Due today" icon={Clock} items={filtered.today} testid="alerts-today" tone="warn" />
              <Section title="Abandoned (no activity 14d+)" icon={Ghost} items={filtered.abandoned} testid="alerts-abandoned" tone="neutral" />
            </>
          )}
        </div>
      )}

      <LeadDetail
        lead={detail}
        open={!!detail}
        onOpenChange={(v) => !v && setDetail(null)}
        members={members}
        onChanged={load}
      />
    </div>
  );
}
